/**
 * The data-field picker. Tapping a data box opens this to change what that box
 * shows; "Add another field" gives a second (or third) box, each set
 * independently, and "Remove this field" takes one away. The boxes stack in the
 * top-right. There's always at least one.
 */
import { useShellStore, type DataField, type SheetName } from '@/store/shell';
import { MAX_DATA_FIELDS } from '@/store/dataFieldConfig';
import { Sheet } from './Sheet';

const OPTIONS: { key: DataField; name: string; detail: string }[] = [
  { key: 'sog', name: 'SOG', detail: 'Speed over ground' },
  { key: 'cog', name: 'COG', detail: 'Course over ground' },
  { key: 'heading', name: 'HDG', detail: 'Heading' },
  { key: 'tide', name: 'TIDE', detail: 'Height of tide now at the reference port' },
  { key: 'xte', name: 'XTD', detail: 'Cross-track distance' },
  { key: 'cpa', name: 'CPA', detail: 'Closest point of approach (nearest AIS)' },
  { key: 'brgwp', name: 'BRG WP', detail: 'Bearing to waypoint' },
  { key: 'dtwp', name: 'DIST WP', detail: 'Distance to waypoint' },
  { key: 'eta', name: 'ETA', detail: 'Time of arrival' },
  { key: 'pos', name: 'POS', detail: 'Position' },
  { key: 'gnss', name: 'GNSS ±', detail: 'GNSS position accuracy' },
  { key: 'uke', name: 'U/KEEL', detail: 'Depth under keel (tide-corrected)' },
  { key: 'trip', name: 'TRIP', detail: 'Distance run along the current track' },
];

/**
 * The detail a field's box can open, inheriting the strip's old tap-to-expand:
 * the Tide box reaches the tide scrubber/curve in Conditions, the CPA box the
 * AIS detail, the XTE box the route. Shown as a button so the box stays
 * configurable (the tap still opens this picker) while nothing is lost.
 */
const FIELD_DETAIL: Partial<Record<DataField, { sheet: SheetName; label: string }>> = {
  tide: { sheet: 'conditions', label: 'Open tide & conditions' },
  cpa: { sheet: 'ais', label: 'Open AIS & CPA detail' },
  xte: { sheet: 'route', label: 'Open route & XTD' },
};

export function DataFieldSheet() {
  const dataFields = useShellStore((s) => s.dataFields);
  const editingFieldIndex = useShellStore((s) => s.editingFieldIndex);
  const setDataFieldAt = useShellStore((s) => s.setDataFieldAt);
  const addDataField = useShellStore((s) => s.addDataField);
  const removeDataField = useShellStore((s) => s.removeDataField);
  const moveDataField = useShellStore((s) => s.moveDataField);
  const pinnedPrimary = useShellStore((s) => s.pinnedPrimary);
  const setPinnedPrimary = useShellStore((s) => s.setPinnedPrimary);
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const index = editingFieldIndex ?? 0;
  const current = dataFields[index];
  const detail = current ? FIELD_DETAIL[current] : undefined;
  const isPinned = current != null && pinnedPrimary === current;
  const atMax = dataFields.length >= MAX_DATA_FIELDS;
  const currentName = OPTIONS.find((o) => o.key === current)?.name ?? 'this field';

  return (
    <Sheet
      name="data"
      title="Data field"
      sub="Pick what this box shows. Add another to keep more than one number on screen at once."
    >
      {detail && (
        <button
          type="button"
          className="acp-btn primary"
          style={{ width: '100%', marginBottom: 10 }}
          onClick={() => {
            openSheet(detail.sheet);
          }}
        >
          {detail.label} →
        </button>
      )}
      {current && (
        <button
          type="button"
          className={`acp-btn${isPinned ? ' primary' : ''}`}
          style={{ width: '100%', marginBottom: 10 }}
          aria-pressed={isPinned}
          onClick={() => {
            if (isPinned) {
              setPinnedPrimary(null);
              showToast('Primary chosen automatically');
            } else {
              setPinnedPrimary(current);
              showToast(`${currentName} pinned as the primary field`);
            }
          }}
        >
          {isPinned
            ? '\u2605 Primary field \u2014 tap to choose automatically'
            : '\u2606 Make this the primary field'}
        </button>
      )}
      {OPTIONS.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`acp-optrow${o.key === current ? ' on' : ''}`}
          aria-pressed={o.key === current}
          onClick={() => {
            setDataFieldAt(index, o.key);
            closeSheet();
            showToast(`Showing ${o.name}`);
          }}
        >
          <span className="k">
            <span className="nm">{o.name}</span>
            <span className="dt">{o.detail}</span>
          </span>
          <span className="chk" aria-hidden>
            {o.key === current ? '✓' : ''}
          </span>
        </button>
      ))}
      {dataFields.length > 1 && (
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            disabled={index <= 0}
            onClick={() => moveDataField(index, -1)}
          >
            Move up
          </button>
          <button
            type="button"
            className="acp-btn"
            disabled={index >= dataFields.length - 1}
            onClick={() => moveDataField(index, 1)}
          >
            Move down
          </button>
        </div>
      )}
      <div className="acp-btn-row">
        {dataFields.length > 1 && (
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              removeDataField(index);
              closeSheet();
              showToast('Field removed');
            }}
          >
            Remove this field
          </button>
        )}
        {atMax ? (
          <span className="acp-hint" style={{ alignSelf: 'center' }}>
            {MAX_DATA_FIELDS} fields is the max, to keep the chart clear.
          </span>
        ) : (
          <button
            type="button"
            className="acp-btn primary"
            onClick={() => {
              addDataField();
              showToast('Pick what it shows');
            }}
          >
            Add another field
          </button>
        )}
      </div>
    </Sheet>
  );
}
